import { useEffect, useState } from "react";
import { Percent, Copy, Ticket } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PowerDeleteButton, usePowerDelete } from "@/context/PowerDeleteContext";

function CouponChip({ service, onGenerated }) {
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/services/${service.id}/coupon`);
      onGenerated(data);
      toast.success(`Free coupon ready: ${data.code} (valid 7 days)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(service.coupon_code);
    toast.success("Coupon code copied");
  };

  if (!service.coupon_code) {
    return (
      <button data-testid={`generate-coupon-${service.id}`} onClick={generate} disabled={busy}
        className="btn-outline text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
        <Ticket className="w-3.5 h-3.5" /> {busy ? "Generating…" : "Coupon"}
      </button>
    );
  }
  return (
    <span className="chip text-xs inline-flex flex-col items-start gap-0.5 py-1.5" style={{ background: "rgba(91,117,83,0.15)", color: "var(--brand)" }}
      data-testid={`admin-coupon-${service.id}`}>
      <span className="inline-flex items-center gap-1.5">
        {service.coupon_code}
        <button data-testid={`copy-coupon-${service.id}`} onClick={copy} title="Copy code" className="hover:scale-110 transition-transform">
          <Copy className="w-3 h-3" />
        </button>
        <PowerDeleteButton collection="coupons" id={service.coupon_id} onDeleted={() => onGenerated(null)} />
      </span>
      {service.coupon_expires_at && (
        <span className="text-[0.6rem] opacity-80">expires {new Date(service.coupon_expires_at).toLocaleDateString()}</span>
      )}
    </span>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }

function ServiceRow({ service, onUpdated }) {
  const { refreshToken } = usePowerDelete();
  const [price, setPrice] = useState(String(service.original_price));
  const [discount, setDiscount] = useState(String(service.discount_percent || 0));
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState({ coupon_code: service.coupon_code, coupon_id: service.coupon_id, coupon_expires_at: service.coupon_expires_at });

  useEffect(() => {
    setPrice(String(service.original_price));
    setDiscount(String(service.discount_percent || 0));
    setCoupon({ coupon_code: service.coupon_code, coupon_id: service.coupon_id, coupon_expires_at: service.coupon_expires_at });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.original_price, service.discount_percent, service.coupon_code, service.coupon_id, service.coupon_expires_at, refreshToken]);

  const dirty = Number(price) !== service.original_price || Number(discount) !== (service.discount_percent || 0);

  const save = async () => {
    const p = Number(price), d = Number(discount);
    if (Number.isNaN(p) || p <= 0) { toast.error("Enter a valid price"); return; }
    if (Number.isNaN(d) || d < 0 || d > 100) { toast.error("Discount must be 0–100"); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/services/${service.id}`, { price: p, discount_percent: d });
      onUpdated(service.id, data);
      toast.success("Package updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const effective = Number(discount) > 0 ? round2(Number(price) * (1 - Number(discount) / 100)) : Number(price);

  return (
    <div className="rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--bg-alt)" }}
      data-testid={`service-row-${service.id}`}>
      <div className="min-w-[160px]">
        <div className="font-medium">{service.name}</div>
        <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
          {Number(discount) > 0 ? (
            <>
              <span className="line-through mr-2">₹{price}</span>
              <span style={{ color: "var(--brand)" }}>₹{effective} ({discount}% off)</span>
            </>
          ) : <>₹{price}</>}
          {dirty && <span className="ml-2 italic" style={{ color: "#8a6a1f" }}>(unsaved — click Save)</span>}
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="label-eyebrow block mb-1 text-[0.6rem]">Rate (₹)</label>
          <input type="number" min="1" data-testid={`service-price-${service.id}`} value={price}
            onChange={e => setPrice(e.target.value)}
            className="w-24 rounded-lg border border-[color:var(--border-soft)] px-2.5 py-1.5 text-sm focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1 text-[0.6rem]">Discount %</label>
          <input type="number" min="0" max="100" data-testid={`service-discount-${service.id}`} value={discount}
            onChange={e => setDiscount(e.target.value)}
            className="w-20 rounded-lg border border-[color:var(--border-soft)] px-2.5 py-1.5 text-sm focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
        <button data-testid={`service-save-${service.id}`} onClick={save} disabled={busy || !dirty}
          className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <CouponChip service={{ id: service.id, coupon_code: coupon.coupon_code, coupon_id: coupon.coupon_id, coupon_expires_at: coupon.coupon_expires_at }}
          onGenerated={(data) => {
            setCoupon({ coupon_code: data?.code, coupon_id: data?.id, coupon_expires_at: data?.expires_at });
            onUpdated(service.id, { coupon_code: data?.code, coupon_id: data?.id, coupon_expires_at: data?.expires_at });
          }} />
        <PowerDeleteButton collection="services" id={service.id} onDeleted={() => onUpdated(service.id, null)} />
      </div>
    </div>
  );
}

export default function AdminServiceDiscountPanel() {
  const { refreshToken } = usePowerDelete();
  const [services, setServices] = useState([]);

  const load = () => api.get("/admin/services").then(r => setServices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, [refreshToken]);

  const onUpdated = (id, updated) => {
    if (!updated) { load(); return; }
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...updated } : s));
  };

  return (
    <div className="card-soft p-8 mb-8" data-testid="admin-discount-panel">
      <h2 className="font-serif-display text-2xl inline-flex items-center gap-2 mb-1">
        <Percent className="w-5 h-5" style={{ color: "var(--brand)" }} /> Package pricing & coupons
      </h2>
      <p className="text-xs mb-5" style={{ color: "var(--text-secondary)" }}>
        Edit each package's rate and discount independently. "Coupon" mints a free redeemable code, valid for 7 days from generation. Attaching it to a client (Manage Users tab) emails them the code and its expiry.
      </p>
      <div className="space-y-2" data-testid="admin-discount-services">
        {services.map(s => <ServiceRow key={s.id} service={s} onUpdated={onUpdated} />)}
      </div>
    </div>
  );
}
